import { Placeholder } from "react-bootstrap";
import goldImage from "../../assets/images/gold-tube-barcode-label.png";
import royalImage from "../../assets/images/royal-mail-tracking-number-label.png";
import { TEST_TYPES, formatSecondaryBarcode } from "../../utils";

// ========================================
// BASE TEMPLATES & UTILITIES
// ========================================

const baseField = {
  as: "",
  required: true,
  placeholder: "",
  size: "text",
  readOnly: false,
  disabled: false,
  className: "form-group",
};

const createField = (overrides) => ({
  ...baseField,
  ...overrides,
});

// ========================================
// COMMON FIELD FACTORIES
// ========================================

const createTextInput = (controlId, label, name, overrides = {}) =>
  createField({
    controlId,
    label,
    type: "text",
    name,
    aria_describedby: `val${controlId}`,
    ...overrides,
  });

const createSelectField = (
  controlId,
  label,
  name,
  options,
  overrides = {},
  required,
) =>
  createField({
    controlId,
    label,
    type: "select",
    required,
    name,
    htmlFor: name,
    options,
    aria_describedby: `val${controlId}`,
    ...overrides,
  });

const createCheckboxField = (
  controlId,
  label,
  name,
  value,
  groupName,
  overrides = {},
) => ({
  controlId,
  label,
  type: "checkbox",
  name,
  value,
  group_name: groupName,
  hide: false,
  aria_describedby: `val${controlId}`,
  className: "form-group",
  ...overrides,
});

const createRadioField = (controlId, label, name, choices, overrides = {}) =>
  createField({
    controlId,
    label,
    type: "radio",
    name,
    required: true,
    choices,
    aria_describedby: `val${controlId}`,
    ...overrides,
  });

const createToggleField = (
  controlId,
  name,
  label,
  options,
  helpText,
  overrides = {},
) => ({
  name,
  controlId,
  label,
  type: "toggle-buttons",
  required: true,
  options,
  helpText,
  className: "toggle-btn-group help-text",
  ...overrides,
});

const createButtonGroup = (
  prevControlId,
  nextControlId,
  prevLabel = "Previous step",
  nextLabel = "Confirm & Continue",
) => ({
  controlId: `${Number.parseInt(nextControlId) + 1}`,
  label: "",
  type: "",
  name: "",
  btnGroup: [
    {
      controlId: prevControlId,
      label: prevLabel,
      type: "button",
      onClick: "",
      size: "md",
      variant: "white",
      className: "btn-login mt-2 btn-gray ageo-semibold btn-layout",
    },
    {
      controlId: nextControlId,
      label: nextLabel,
      type: "submit",
      size: "md",
      variant: "green",
      className: "btn-login mt-2 ageo-semibold btn-layout",
    },
  ],
});

const createSubmitButton = (
  label = "Confirm & Continue",
  controlId = "confirmButton",
  className = "btn m-2 ageo-semibold",
) => ({
  controlId,
  label,
  type: "submit",
  size: "large",
  className,
  variant: "green",
});

const createTextareaField = (
  controlId,
  label,
  name,
  placeholder,
  overrides = {},
) =>
  createField({
    controlId,
    label,
    name,
    maxLength: 256,
    placeholder,
    aria_describedby: `val${controlId}`,
    hide: true,
    required: false,
    ...overrides,
  });

const createCheckboxGroup = (items, groupName, startIndex = 1) =>
  items.map((item, index) =>
    createCheckboxField(
      String(index + startIndex),
      item,
      groupName,
      item,
      `${groupName}_group`,
      { hide: true },
    ),
  );

// ========================================
// COMMON CONFIGURATIONS
// ========================================

const today = new Date();
const defaultDateFor18YearsOld = new Date(
  today.setFullYear(today.getFullYear() - 18),
);
const formattedDefaultDate = defaultDateFor18YearsOld
  .toISOString()
  .split("T")[0];

const HELP_TEXTS = {
  phone:
    "We ask for your phone number because, on rare occasions, our doctors may need to reach out to you.",
  phoneSexualHealth:
    "At MyHealthChecked, we require a phone number for patients who purchase sexual health tests. This is to ensure our doctors can reach you with your results and any necessary follow-up care in a timely and confidential manner.\nPlease note that providing your phone number does not indicate a positive result. Patient privacy and safeguarding are top priorities for us, and we handle your information with the utmost care.",
  dob: "Our products and services are designed for individuals who are 18 years old and over.",
  sex: "We ask this to ensure accurate results.",
  genderIdentity:
    "We ask this because hormone therapy related to gender transition may affect your results.",
  marketing:
    "Our team of medical doctors and nutritionists is here to support your health journey, and we love to send simple short expert blogs, product news, and motivational topics straight to your inbox. You can unsubscribe at any time, and we'll never share your personal data.",
  retestKitOrder:
    "If you would prefer to claim a refund for the eligible amount, please close this window and follow the instructions in the notification email.",
  doNotHaveUkNumber: `I don't have a UK number`
};

const COMMON_OPTIONS = {
  yesNo: ["Yes", "No"],
  yesNoButtons: [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ],
  gender: [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
  ],
  genderWithPreferNot: [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
  ],
  genderIdentity: [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ],
  genderIdentityTrue: [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ],
  ethnicity: [
    { value: "white", label: "White" },
    { value: "asian", label: "Asian" },
    { value: "black", label: "Black, African, Caribbean or Black British" },
    { value: "mixed", label: "Mixed ethnic group" },
    { value: "other", label: "Other ethnic group" },
  ],
};

// ========================================
// REUSABLE FIELD CONFIGURATIONS
// ========================================

const createGenderField = (
  helpText = HELP_TEXTS.sex,
  options = COMMON_OPTIONS.gender,
) => createToggleField("gender", "sex", "Sex at birth", options, helpText);

const createGenderIdentityField = (
  helpText = HELP_TEXTS.genderIdentity,
  options = COMMON_OPTIONS.genderIdentity,
) =>
  createToggleField(
    "confirmation",
    "identify_with_gender",
    "Do you still identify with this gender?",
    options,
    helpText,
  );

const createEthnicityField = (controlId = "ethnicity") =>
  createRadioField(
    controlId,
    "Ethnicity",
    "ethnicity",
    COMMON_OPTIONS.ethnicity,
    {
      htmlFor: controlId,
      className: "form-group",
    },
  );

const createPhoneField = (helpText = HELP_TEXTS.phone, extraClass = "") => ({
  controlId: "phone_number_primary",
  name: "phone_number_primary",
  placeholder: "Main phone number",
  type: "phone",
  maxLength_length: 30,
  helpText,
  className: `helptext ${extraClass}`.trim(),
});

const createDobField = (controlId = "dob", placeholder = "Date of birth") => ({
  controlId,
  placeholder,
  type: "date",
  className: "form-group helptext error-toggle",
  name: "dob",
  max: formattedDefaultDate,
  helpText: HELP_TEXTS.dob,
  label: "Date of birth",
  required: true,
});

const createPostcodeField = (label = "", disabled = false) => ({
  controlId: "home_postal_code",
  name: "home_postal_code",
  placeholder: "Your postcode",
  min_length: 1,
  maxLength: 10,
  label: label,
  disabled: disabled,
});

const createAddressSelectField = (controlId, label, disabled = false) =>
  createSelectField(controlId, label, "home_street_address", [], {
    hide: false,
    placeholder: "Select address",
    className: "select-form",
    disabled: disabled,
  });

// ========================================
// COMMON FIELD PATTERNS
// ========================================

const createNameFields = (disabled = false) => [
  createTextInput("first_name", "First name", "first_name", {
    maxLength: 63,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "First name",
    disabled,
  }),
  createTextInput("last_name", "Last name", "last_name", {
    maxLength: 63,
    aria_describedby: "val2",
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Last name",
    disabled,
  }),
];

const createConsentCheckbox = (
  controlId,
  label,
  name,
  value = true,
  helpText = null,
) => ({
  controlId,
  label,
  noLabel: true,
  type: "checkbox",
  name,
  value,
  as: "",
  required: controlId !== "7",
  placeholder: "",
  size: "text",
  readOnly: false,
  disabled: false,
  aria_describedby: "val2",
  className: "form-group helptext checkbox-consent",
  helpTextClass:
    controlId === "7"
      ? "mb-3 pb-1 mt-0 d-inline-block"
      : "ms-4 mb-2 mt-0 d-inline-block",
  ...(helpText && { helpText }),
  ...(controlId === "6" && { checked: false }),
});

// ========================================
// AUTHENTICATION FIELDS
// ========================================

export const loginFields = [
  createField({
    controlId: "1",
    required: false,
    placeholder: "Email address",
    type: "email",
    name: "email",
    aria_describedby: "val1",
  }),
  createField({
    controlId: "2",
    required: false,
    placeholder: "Password",
    type: "password",
    name: "password",
    aria_describedby: "val2",
    links: (
      <div className="forgot-password-link">
        <a className="dark-grey-text ageo-medium" href="/reset-password">
          Forgot your password?
        </a>
      </div>
    ),
  }),
  {
    controlId: "3",
    label: "Sign in",
    type: "submit",
    size: "large",
    variant: "primary",
    className: "btn m-2 sign-in-btn w-100",
    links: (
      <div className="create-account-link">
        Don't have an account?{" "}
        <a className="text-secondary" href="/create-account">
          Sign up
        </a>
      </div>
    ),
  },
];

export const setPasswordFields = [
  createField({
    controlId: "1",
    label: "Email",
    type: "email",
    name: "email",
    placeholder: "Enter your email id",
    aria_describedby: "val1",
    readOnly: true,
    disabled: true,
  }),
  createField({
    controlId: "2",
    label: "Password",
    type: "password",
    name: "password",
    placeholder: "Enter your password",
    size: "large",
    aria_describedby: "val2",
    maxLength: 16,
  }),
  createField({
    controlId: "3",
    label: "Repeat password",
    type: "password",
    name: "confirm_password",
    placeholder: "Confirm your password",
    size: "large",
    aria_describedby: "val2",
    maxLength: 16,
  }),
  createSubmitButton("Reset Password", "4", "btn-reset-password"),
];

export const MfaFields = [
  {
    controlId: "1",
    label: "",
    type: "phone",
    name: "primary_contact_no",
    as: "",
    required: true,
    placeholder: "",
    size: "text",
    readOnly: false,
    disabled: false,
    aria_describedby: "val1",
    helpText: HELP_TEXTS.doNotHaveUkNumber,
    className: "form-group my-0 enableMFA",
  },
];
export const MfaEmailFields = [
  {
    controlId: "1",
    label: "",
    type: "email",
    name: "mfa_email",
    identity: "mfaEmail",
    as: "",
    required: false,
    placeholder: "",
    size: "text",
    readOnly: true,
    disabled: true,
    isDisabled: true,
    aria_describedby: "val1",
    helpText: HELP_TEXTS.doNotHaveUkNumber,
    className: "form-group my-0 enableMFA emailMFA",
  },
];
export const OtpFields = [
  {
    controlId: "1",
    label: "",
    type: "number",
    name: "verifyOtp",
    as: "",
    required: true,
    maxLength: "6",
    placeholder: "Enter OTP",
    size: "text",
    readOnly: false,
    disabled: false,
    aria_describedby: "val1",
    className: "form-group my-0",
  },
];

export const resetPassword = [
  createField({
    controlId: "1",
    placeholder: "Email address",
    label: "",
    type: "email",
    name: "email",
    aria_describedby: "val1",
  }),
  {
    controlId: "recaptcha",
    type: "recaptcha",
    name: "recaptcha_token",
    required: true,
  },
  createSubmitButton(
    "Reset password",
    "2",
    "btn btn-green full-btn fs-16 btn-reset-password",
  ),
];

// ========================================
// CREATE ACCOUNT FIELDS
// ========================================

const accountDetailsGroup = {
  type: "group",
  label: "1. Account details",
  required: false,
  className: "create-account-fields",
  children: [
    createField({
      controlId: "1",
      name: "email",
      type: "email",
      label: "",
      placeholder: "Email address",
      className: "w-100 w-md-50",
      maxLength: 255,
    }),
    createField({
      controlId: "2",
      name: "password",
      type: "password",
      label: "",
      placeholder: "Password",
      className: "w-100 w-md-50",
      maxLength: 16,
    }),
  ],
};

const personalInfoGroup = {
  type: "group",
  label: "2. Personal information",
  className: "personal-information-fields",
  required: false,
  children: createNameFields(),
};

const removeRequired = (fields) =>
  fields.map((field) => ({
    ...field,
    required: false,
    children: field.children ? removeRequired(field.children) : field.children,
    check_box_group: field.check_box_group
      ? removeRequired(field.check_box_group)
      : field.check_box_group,
  }));

export const createAccount = removeRequired([
  accountDetailsGroup,
  personalInfoGroup,
  createPhoneField(HELP_TEXTS.phone, "create38"),
  createDobField("dob", "Select date of birth"),
  createPostcodeField(),
  createAddressSelectField("home_street_address"),
  createTextInput("address_line_1", "Address line 1", "address_line_1", {
    maxLength: 256,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Address line 1",
    required: false,
    disabled: false,
    hide: true,
  }),
  createTextInput("address_line_2", "Address line 2", "address_line_2", {
    maxLength: 256,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Address line 2",
    required: false,
    disabled: false,
    hide: true,
  }),
  createTextInput("home_city", "Town/City", "home_city", {
    maxLength: 63,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Town/City",
    isDisabled: true,
    required: false,
    disabled: false,
    hide: true,
  }),
  createTextInput("home_country", "County/Region", "home_country", {
    maxLength: 256,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "County/Region",
    required: false,
    hide: true,
    value: "UK",
    disabled: true,
    readOnly: true,
  }),
  createGenderField(),
  createGenderIdentityField(),
  createEthnicityField(),
  {
    controlId: "5",
    ...createConsentCheckbox(
      "5",
      <>
        {" "}
        <span>
          I have read and agree to the{" "}
          <a
            className="berry-link-underline"
            href="https://myhealthchecked.com/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Terms & Conditions</span>
          </a>
          <span>.</span>
        </span>
      </>,
      "service_consent",
    ),
  },
  {
    controlId: "6",
    ...createConsentCheckbox(
      "6",
      <>
        {" "}
        <span>
          I consent to the processing of my personal data in accordance with the{" "}
          <a
            className="berry-link-underline"
            href="https://myhealthchecked.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Privacy Policy</span>
          </a>
          <span>.</span>
        </span>
      </>,
      "policy_consent",
    ),
  },
  createConsentCheckbox(
    "7",
    "Yes, I'd like to receive health tips, product updates, and offers by email.",
    "marketing_consent",
    true,
    HELP_TEXTS.marketing,
  ),
  {
    controlId: "recaptcha",
    name: "recaptcha_token",
    type: "recaptcha",
    required: true,
  },
  createSubmitButton(
    "Create account",
    "9",
    "btn-create-account btn-reset-password btn-green",
  ),
]);

// ========================================
// ASSESSMENT FIELDS
// ========================================

const createMeasurementField = (
  controlId,
  label,
  name,
  unitOptions,
  unitName,
) => ({
  type: "inline-group",
  className: "mb-3",
  label,
  required: true,
  children: [
    createField({
      controlId,
      label: "",
      type: "number",
      name,
      htmlFor: name,
      maxLength: 4,
      aria_describedby: `val${controlId}`,
      placeholder: "Eg., 175",
      className: "form-control",
      required: true,
    }),
    {
      name: unitName,
      controlId: String(Number(controlId) + 1),
      label: "",
      type: "toggle-buttons",
      convert: true,
      required: false,
      options: unitOptions,
      className: "toggle-btn-group",
      aria_describedby: `val${Number(controlId) + 1}`,
    },
  ],
});

export const commonBodyFields = [
  {
    type: "section-card",
    title: "Height and weight",
    className: "mobile-card",
    children: [
        createField({
      controlId:"1",
      label: "Please enter your height (cm)",
      type: "number",
      name:"height",
      htmlFor: "height",
      maxLength: 4,
      aria_describedby: "val1",
      placeholder: "Eg., 175",
      className: "form-control",
      required: true,
    }),
      createMeasurementField(
        "3",
        "Please enter your weight",
        "weight",
        [
          { value: "metric", label: "kg" },
          { value: "imperial", label: "lbs" },
        ],
        "weightUnits",
      ),
      {
        type: "inline-group",
        className: "mb-3",
        label: "Are you pregnant?",
        required: true,
        hide: true,
        children: [
          {
            name: "ispregnant",
            hide: true,
            controlId: "5",
            label: "",
            type: "toggle-buttons",
            convert: true,
            options: COMMON_OPTIONS.yesNoButtons,
            className: "toggle-btn-group",
            aria_describedby: "val5",
          },
        ],
      },
      createSelectField("15", "", "trimester", ["1", "2", "3"], {
        hide: true,
        placeholder: "Select trimester",
        aria_describedby: "val15",
        required: false,
      }),
    ],
  },
  {
    type: "section-card",
    title: "Smoking",
    className: "mobile-card body-form",
    children: [
      {
        controlId: "6",
        name: "smoking",
        label: "Do you smoke cigarettes?",
        type: "radio",
        required: true,
        choices: [
          { value: "Yes", label: "Yes" },
          { value: "No", label: "No" },
          { value: "Ex-smoker", label: "Ex-smoker" },
        ],
        aria_describedby: "val6",
      },
      createSelectField(
        "7",
        "How many cigarettes (or equivalent) per day?",
        "cigarettes_per_day",
        ["0 - 5", "6 - 10", "11 - 20", "20+"],
        {
          hide: true,
          placeholder: "Select an option",
          aria_describedby: "val7",
        },
      ),
      createField({
        controlId: "8",
        label: "How many years did you smoke?",
        type: "number",
        name: "how_many_year",
        htmlFor: "how_many_year",
        maxLength: 3,
        placeholder: "Eg., 12 years",
        aria_describedby: "val8",
        hide: true,
      }),
      createField({
        controlId: "9",
        label: "How many per day on average?",
        type: "number",
        name: "how_many_day_average",
        htmlFor: "how_many_day_average",
        maxLength: 3,
        placeholder: "Eg., 12",
        aria_describedby: "val9",
        hide: true,
      }),
    ],
  },
  {
    type: "section-card",
    title: "Alcohol",
    className: "mobile-card body-form",
    children: [
      {
        controlId: "10",
        name: "alcohol",
        label: "Do you drink alcohol?",
        type: "radio",
        required: true,
        choices: [
          { value: "Yes", label: "Yes" },
          { value: "No", label: "No" },
          { value: "I recently quit", label: "I recently quit" },
        ],
        aria_describedby: "val10",
      },
      createSelectField(
        "alcohol_units_per_week",
        "How many units per week?",
        "alcohol_units_per_week",
        ["0 - 7", "7 - 14", "15 - 30", "30+"],
        {
          hide: true,
          placeholder: "Select an option",
          helpText: `If you're unsure how to calculate, visit: [NHS LINK](https://www.nhs.uk/Live-well/alcohol-advice/calculating-alcohol-units/)`,
          aria_describedby: "val11",
        },
      ),
    ],
  },
];

export const bodyFields = [
  ...commonBodyFields,
  createSubmitButton("Confirm & Continue", "", "Confirm & Continue btn-layout"),
];

export const phelobotomistBodyFields = [
  ...commonBodyFields,
  createButtonGroup("5", "6"),
];

// ========================================
// EXERCISE FIELDS
// ========================================

const createActivitySelectField = (controlId, label, name) =>
  createSelectField(
    controlId,
    label,
    name,
    ["0", "1", "2", "3", "4", "5", "6", "7", "7+"],
    {
      hide: true,
      helpText:
        "Please tell us how many hours you spent on physical activity last week",
      aria_describedby: `val${controlId}`,
      placeholder: "Select an option",
    },
  );

export const exerciseFields = [
  {
    type: "section-card",
    title: "Physical activity",
    className: "mobile-card physical-activity-wrapper",
    children: [
      createRadioField(
        "1",
        "Do you do any physical activity?",
        "physical_activity",
        COMMON_OPTIONS.yesNo.map((v) => ({ value: v, label: v })),
        {
          aria_label: "physical_activity",
        },
      ),
      createActivitySelectField(
        "2",
        "Light activity, e.g. walking, stretching (in hours)",
        "light_activity_hours_per_week",
      ),
      createActivitySelectField(
        "3",
        "Moderate activity, e.g. aerobics, golf (in hours)",
        "moderate_activity_hours_per_week",
      ),
      createActivitySelectField(
        "4",
        "Vigorous activity, e.g. running, weight training (in hours)",
        "vigorous_activity_hours_per_week",
      ),
    ],
  },
  createButtonGroup("5", "6"),
];

// ========================================
// MEDICAL CONDITIONS & MEDICATIONS
// ========================================

const MEDICAL_CONDITIONS = [
  "Anaemia",
  "Cancer",
  "Asthma",
  "Chronic Kidney Disease",
  "Coeliac Disease",
  "Diabetes",
  "Dementia",
  "Gout",
  "Heart disease",
  "High blood pressure",
  "IBS",
  "IBD",
  "Irregular heart rhythm",
  "Liver disease",
  "Mental health problems",
  "PCOS",
  "Menopause",
  "Perimenopause",
  "Rheumatoid arthritis",
  "Thyroid problems",
  "Stroke / TIA",
  "Vitamin/mineral deficiency",
  "Other",
];

const MEDICATIONS = [
  "Blood pressure",
  "Cholesterol-lowering",
  "Contraceptives",
  "Diabetes (incl. insulin)",
  "Diuretics",
  "Heart medication",
  "HRT",
  "Mental health",
  "Steroids",
  "Thyroxine",
  "Immunosuppressants (e.g. methotrexate)",
  "Other",
];

const SUPPLEMENTS = [
  "Vitamin D",
  "Iron, Folate",
  "Vitamin B12",
  "Magnesium",
  "Omega 3",
  "Calcium",
  "Multivitamin",
  "Other",
];

const createCheckboxSection = (
  items,
  groupName,
  startIndex,
  label = "Please tick all that apply:",
) => ({
  name: groupName,
  label,
  check_box_group: [
    {
      controlId: String(startIndex),
      label,
      type: "group-label",
      hide: true,
      name: groupName,
      required: false,
      className: "form-group",
    },
    ...createCheckboxGroup(items, groupName, startIndex + 1),
  ],
});

const createConditionSection = (
  title,
  selectName,
  selectControlId,
  checkboxItems,
  checkboxGroupName,
  label,
) => ({
  type: "section-card",
  title,
  className: "mobile-card my-3 medication-section-wrapper",
  children: [
    createSelectField(
      selectControlId,
      label || "",
      selectName,
      COMMON_OPTIONS.yesNo,
      {
        hide: false,
        placeholder: "Please choose",
        controlId: selectControlId,
        label: label || "",
        sectionTitle: title,
      },
    ),
    createCheckboxSection(checkboxItems, checkboxGroupName, 2),
  ],
});

// Helper to create condition section with additional fields
const createConditionSectionWithFields = (
  title,
  selectName,
  selectControlId,
  checkboxItems,
  checkboxGroupName,
  label,
  additionalFields = [],
) => {
  const baseSection = createConditionSection(
    title,
    selectName,
    selectControlId,
    checkboxItems,
    checkboxGroupName,
    label,
  );

  return {
    ...baseSection,
    children: [...baseSection.children, ...additionalFields],
  };
};

export const medicalConditionFields = [
  createConditionSectionWithFields(
    "Medical conditions",
    "was_diagnosed",
    "1",
    MEDICAL_CONDITIONS,
    "condition",
    "Have you been diagnosed with any medical conditions?",
    [
      createTextareaField(
        "3",
        "Please specify cancer type and whether you are currently or recently (past 3 months) under treatment (chemo, radio, or immunotherapy).",
        "cancer_type",
        "Eg., breast cancer, currently receiving chemo.",
        { hide: true, required: false },
      ),
      createTextareaField(
        "4",
        "Please specify which deficiency",
        "deficiency",
        "E.g., migraine medication, vitamin supplements",
        { hide: true, required: false },
      ),
      createTextareaField(
        "5",
        "Please specify",
        "other_description",
        "Please specify conditions here",
        { hide: true, required: false },
      ),
    ],
  ),
];

export const medicationFields = [
  createConditionSectionWithFields(
    "Medications",
    "medication",
    "0",
    MEDICATIONS,
    "medications",
    "Are you taking any regular medication?",
    [
      createTextareaField(
        "11",
        "Please tell us the name, dosage, and frequency of your medication.",
        "specify_medication",
        "E.g., Name: Lisinopril, Dosage: 10 mg, Frequency: once daily",
      ),
      createTextareaField(
        "24",
        "Please specify",
        "medical_other_description",
        "E.g., migraine medication, vitamin supplements",
        { aria_describedby: "val12" },
      ),
    ],
  ),
];

export const supplementFields = [
  createConditionSectionWithFields(
    "Supplementations",
    "taking_supplements",
    "0",
    SUPPLEMENTS,
    "supplements",
    "Are you taking any regular supplements?",
    [
      createTextareaField(
        "12",
        "Please tell us the name, dosage, and frequency of your medication.",
        "supplement_details",
        "E.g., Name: Lisinopril, Dosage: 10 mg, Frequency: once daily",
      ),
      createTextareaField(
        "11",
        "Please specify",
        "supplement_other_description",
        "E.g., migraine medication, vitamin supplements",
      ),
    ],
  ),
  createButtonGroup("13", "14"),
];

// ========================================
// MENSTRUAL & SEXUAL HEALTH
// ========================================

export const menstrualCycleFields = [
  {
    type: "section-card",
    title: "",
    className: "mobile-card menstrual-form",
    children: [
      createSelectField(
        "1",
        "What is your normal cycle length?",
        "cycle_length",
        [
          "Less than 21 days",
          "21-26 days",
          "27-29 days",
          "30-40 days",
          "More than 40 days",
          "It's not regular",
          "Menopausal",
          "Pregnant",
        ],
        {
          placeholder: "Please choose",
        },
      ),
      createSelectField(
        "2",
        "Which day of your cycle was the test taken on?",
        "day_of_cycle",
        [
          "1 - 5",
          "6 - 14",
          "15 - 21",
          "22 - 28",
          "29 - 40",
          "40+",
          "Not sure",
          "Menopausal",
          "Pregnant",
        ],
        { aria_describedby: "val2", placeholder: "Please choose" },
      ),
      createSelectField(
        "3",
        "Are you taking contraception?",
        "contraception",
        [
          "No",
          "Combined pill",
          "Mini pill",
          "Contraceptive implant",
          "Contraceptive injection",
          "IUS or hormonal coil",
          "IUD or copper coil",
          "Other",
        ],
        { aria_describedby: "val3", placeholder: "Please choose" },
      ),
    ],
  },
  createButtonGroup("5", "6"),
];

const STI_EXPOSURE_TYPES = [
  "Chlamydia",
  "Gonorrhoea",
  "Herpes",
  "HIV",
  "HPV",
  "Syphilis",
  "Other",
];
const UNPROTECTED_SEX_TYPES = ["Oral", "Vaginal", "Anal"];

const createYesNoToggleGroup = (controlId, label, name) => ({
  type: "inline-group",
  controlId,
  className: "mb-3 pt-3",
  label,
  required: true,
  name,
  aria_describedby: `val${controlId}`,
  hide: true,
  children: [
    {
      name,
      controlId,
      type: "toggle-buttons",
      convert: true,
      required: false,
      options: COMMON_OPTIONS.yesNoButtons,
      className: "toggle-btn-group",
      aria_describedby: `val${controlId}`,
    },
  ],
});

const createCheckboxGroupField = (items, name, groupName, startIndex) => ({
  check_group_label: "",
  group_label: "checkboxes",
  name,
  htmlFor: name,
  check_group_required: true,
  check_group_hide: true,
  check_box_group: items.map((item, index) =>
    createCheckboxField(
      String(index + startIndex),
      item,
      name,
      item,
      groupName,
      { noLabel: index === 0, required: false },
    ),
  ),
});

export const sexualHealthFields = [
  {
    type: "section-card",
    title: "",
    className: "mobile-card",
    children: [
      {
        controlId: "1",
        name: "sexually_active",
        label: "Are you sexually active?",
        type: "radio",
        required: true,
        choices: COMMON_OPTIONS.yesNoButtons,
        aria_describedby: "val1",
      },
      createSelectField(
        "2",
        "Partner gender",
        "partners",
        ["Men", "Women", "Both"],
        {
          hide: true,
          aria_describedby: "val2",
          placeholder: "Select an option",
        },
      ),
      createSelectField(
        "3",
        "When was your last sexual activity?",
        "recent_sex",
        [
          "Less than a week ago",
          "In the past month",
          "2-3 months ago",
          "More than 3 months ago",
        ],
        {
          hide: true,
          aria_describedby: "val3",
          placeholder: "Select an option",
        },
      ),
      createYesNoToggleGroup(
        "4",
        "Unprotected sex in past 3 months?",
        "had_unprotected_sex",
      ),
      createCheckboxGroupField(
        UNPROTECTED_SEX_TYPES,
        "unprotected_sex_type",
        "unprotected_sex_type",
        9,
      ),
      createSelectField(
        "5",
        "Have you ever been diagnosed with an STI?",
        "sti_diagnosed",
        [
          "No",
          "Yes, currently diagnosed",
          "Yes, previously diagnosed but fine now",
        ],
        { hide: true, placeholder: "Select an option" },
      ),
      createYesNoToggleGroup("6", "Do you have any symptoms?", "sti_symptoms"),
      createField({
        controlId: "7",
        label: "",
        name: "sti_symptoms_description",
        htmlFor: "sti_symptoms_description",
        placeholder: "Please specify",
        hide: true,
        maxLength: 256,
        aria_describedby: "val7",
        className: "sti-field",
      }),
      createYesNoToggleGroup(
        "6",
        "Do you think you may have been exposed to an STI?",
        "potential_sti_exposure",
      ),
      createCheckboxGroupField(
        STI_EXPOSURE_TYPES,
        "potential_sti_exposure_type",
        "potential_sti_exposure_type",
        9,
      ),
      createField({
        controlId: "9",
        label: "Please specify other STI exposure type",
        name: "other_sti_exposure_type",
        htmlFor: "other_sti_exposure_type",
        placeholder: "Please specify",
        hide: true,
        maxLength: 256,
        aria_describedby: "val9",
      }),
    ],
  },
  createButtonGroup("17", "18", undefined, "Assessment submit"),
];

// ========================================
// BARCODE FIELDS
// ========================================

export const secondaryBarcodeFields = (
  primaryBarcode,
  onSubmit,
  secondaryBarcodeId,
) => [
  {
    name: "secondary_barcode",
    label: "Secondary barcode",
    type: "text",
    value:
      formatSecondaryBarcode(secondaryBarcodeId) ||
      "Secondary barcode not found",
    readOnly: true,
    disabled: false,
  },
  {
    controlId: "confirmButton",
    type: "button-group",
    buttons: [
      {
        label: "No it's not correct",
        type: "submit",
        size: "large",
        className:
          "secondary-barcode-buttons ageo-semibold btn-layout btn-white",
        variant: "grey",
        href: "https://myhealthchecked.com/help",
      },
      {
        label: "Confirm & Continue",
        type: "submit",
        size: "large",
        className: "secondary-barcode-buttons ageo-semibold btn-layout",
        variant: "primary",
        onClick: onSubmit,
      },
    ],
    className: "barcode-2-btn-group",
  },
];

const createBarcodeFields = (
  barcodeLabel,
  barcodeImage,
  barcodePlaceholder,
) => [
  {
    controlId: "barcode",
    testId: "barcode-input",
    label: barcodeLabel,
    type: "text",
    name: "code",
    htmlFor: "barcode",
    required: true,
    ...(barcodeImage && { image: barcodeImage }),
    className: "form-group",
    helpText: "10 character code printed on the sticker.",
    placeholder: barcodePlaceholder,
  },
  {
    controlId: "trackingNumber",
    htmlFor: "trackingNumber",
    label: "Royal mail tracking number (required)",
    type: "text",
    name: "trackingNumber",
    required: true,
    image: royalImage,
    imgClassname: "mt-2 tracking-img",
    className: "form-group",
    helpText:
      "This number can be found on the royal mail return label included in your kit.",
    placeholder: "AA 1234 5678 9BB",
  },
  createSubmitButton(),
];

export const activateKitFields = [
  {
    controlId: "barcode",
    testId: "barcode-input",
    label:
      "Please enter your 10-character barcode to get started with your test registration.",
    type: "text",
    name: "code",
    htmlFor: "barcode",
    required: true,
    // helpTextLabel:
    // "If your test kit includes two barcodes, please use the barcode with the <span style='color:#D5B069;'>GOLD</span> label.",
    helpTextLabel: (
      <>
        If your test kit includes two barcodes, please use the barcode with the{" "}
        <span style={{ color: "#D5B069" }}>GOLD</span> label.
      </>
    ),
    className: "form-group",
    helpText: "10-character code printed label.",
    placeholder: "__________",
  },
  {
    controlId: "trackingNumber",
    htmlFor: "trackingNumber",
    label:
      "Please enter your tracking number. If it’s already filled in, you’re all set!",
    type: "text",
    name: "trackingNumber",
    required: true,
    imgClassname: "mt-2 tracking-img",
    className: "form-group",
    helpText:
      "This number can be found on the return label included in your kit.",
    placeholder: "Eg., AA 1234 5678 9BB",
  },
  createSubmitButton(
    "Confirm & Continue",
    "create-btn-wrapper w-0",
    "Confirm & Continue btn-layout",
  ),
];

export const bloodFields = createBarcodeFields(
  "Gold barcode (required)",
  goldImage,
  "ABC-abc-1234",
);
export const shFields = createBarcodeFields(
  "Gold barcode (required)",
  goldImage,
  "ABC-abc-1234",
);
export const dnaFields = createBarcodeFields(
  "DNA barcode (required)",
  null,
  "__________",
);

// ========================================
// CONFIRM DETAILS FIELDS
// ========================================

const createPersonalDetailsFields = (
  phoneHelpText,
  includeGenderIdentity = true,
  extraClass = "",
) => {
  const fields = removeRequired([
    {
      type: "group",
      className: "",
      children: createNameFields(),
    },
    createPhoneField(HELP_TEXTS.phone, extraClass),
    {
      controlId: "007",
      type: "date",
      htmlFor: "dob",
      className: "form-group",
      name: "dob",
      max: formattedDefaultDate,
      helpText: HELP_TEXTS.dob,
      label: "Date of birth",
      required: false,
    },
    createPostcodeField(),
    createAddressSelectField("home_street_address"),
    createTextInput("address_line_1", "Address line 1 ", "address_line_1", {
      maxLength: 256,
      label: "",
      className: "w-100 w-md-50",
      placeholder: "Address line 1",
      required: true,
      disabled: true,
      hide: true,
    }),
    createTextInput("address_line_2", "Address line 2", "address_line_2", {
      maxLength: 256,
      label: "",
      className: "w-100 w-md-50",
      placeholder: "Address line 2",
      required: false,
      disabled: true,
      hide: true,
    }),
    createTextInput("home_city", "Town/City", "home_city", {
      maxLength: 63,
      label: "",
      className: "w-100 w-md-50",
      placeholder: "Town/City",
      required: true,
      disabled: true,
      hide: true,
    }),
    createTextInput("home_country", "County/Region", "home_country", {
      maxLength: 3,
      label: "",
      className: "w-100 w-md-50",
      placeholder: "County/Region",
      value: "UK",
      required: false,
      hide: true,
      disabled: true,
    }),
    createGenderField(),
    ...(includeGenderIdentity ? [createGenderIdentityField()] : []),
    createEthnicityField("Ethnicity-personal-details"),
    {
      controlId: "personal-details-save",
      className:
        "confirm-button Confirm & Continue btn-green personal-save-changes-btn",
      name: "cancelButton",
      label: "Save changes",
      type: "submit",
      value: "save_changes",
    },
  ]);

  return fields;
};

export const accountDetailsFields = createPersonalDetailsFields(
  HELP_TEXTS.phone,
  true,
  "account41",
);
export const confirmDetailsFields = createPersonalDetailsFields(
  HELP_TEXTS.phone,
  true,
  "account46",
);
export const confirmDetailsFieldsSh = createPersonalDetailsFields(
  HELP_TEXTS.phoneSexualHealth,
  true,
  "account38",
);
export const confirmDetailsFieldsDna = createPersonalDetailsFields(
  HELP_TEXTS.phone,
  true,
  "account38",
);

// ========================================
// OTHER FIELDS
// ========================================

export const timeAdministeredFields = [
  {
    label: "",
    controlId: "sample_collection_date",
    type: "date",
    name: "sample_collection_date",
    htmlFor: "sample_collection_date",
    placeholder: "dd/mm/yyyy",
    readOnly: false,
    maxLengthLength: 8,
    className: "pe-0 date-col",
    colSize: 6,
  },
  {
    label: "",
    type: "time",
    htmlFor: "sample_collection_time",
    name: "sample_collection_time",
    placeholder: "hh:mm",
    readOnly: false,
    required: false,
    className: "ps-0 time-col",
    colSize: 6,
  },
];

export const essentialTipsFields = [
  createSubmitButton("Continue", "confirmButton", "mt-3"),
];

export const accountFields = [
  {
    controlId: "first_name",
    name: "first_name",
    htmlFor: "first_name",
    label: "First name",
    maxLength: 63,
    type: "text",
    placeholder: "Enter your first name",
    required: true,
  },
  {
    controlId: "last_name",
    name: "last_name",
    label: "Last name",
    maxLength: 63,
    type: "text",
    placeholder: "Enter your last name",
    required: true,
  },
  {
    controlId: "email",
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "Enter your email",
    required: true,
    disabled: true,
    maxLength: 255,
  },
  {
    controlId: "dob-account",
    name: "dob",
    label: "Date of birth",
    type: "date",
    placeholder: "Enter your date of birth",
    max: formattedDefaultDate,
  },
  {
    controlId: "phone_number_primary",
    name: "phone_number_primary",
    label: "Main phone number",
    type: "phone",
    maxLength_length: 30,
    placeholder: "Enter a valid UK phone number",
  },
  {
    controlId: "phone_number_secondary",
    name: "phone_number_secondary",
    label: "Alternative phone number",
    type: "tel",
    maxLength_length: 30,
  },
  createGenderField(HELP_TEXTS.sex, COMMON_OPTIONS.genderWithPreferNot),
  createGenderIdentityField(
    HELP_TEXTS.genderIdentity,
    COMMON_OPTIONS.genderIdentityTrue,
  ),
  createEthnicityField(),
  createPostcodeField("Postcode"),
  createAddressSelectField("home_street_address", "Home address"),
  {
    label: "Address line 1",
    controlId: "address_line_1",
    name: "address_line_1",
    placeholder: "Address line 1",
  },
  {
    label: "Address line 2",
    controlId: "address_line_2",
    name: "address_line_2",
    placeholder: "Address line 2",
  },
  {
    controlId: "home_city",
    name: "home_city",
    label: "Home city",
    placeholder: "Enter your home city",
  },
  {
    controlId: "home_country",
    name: "home_country",
    label: "Home country",
    fmt: "as-country",
    type: "text",
    placeholder: "Select your home country",
    disabled: true,
    readOnly: true,
  },
  {
    controlId: "passport_number",
    name: "passport_number",
    label: "Passport number",
    fmt: "inline-code",
    autocomplete: "off",
    min_length: 5,
    maxLength: 15,
  },
  {
    controlId: "marketing_consent",
    name: "marketing_consent",
    label: "Marketing consent",
    type: "checkbox",
    helpText:
      "Tick this box to receive emails with news and offers from MyHealthChecked. We won't share your personal data and you can unsubscribe at any time.",
  },
  {
    controlId: "cancelButton",
    name: "cancelButton",
    label: "Cancel",
    type: "cancel",
    variant: "secondary",
    visibleInDisplay: false,
  },
  {
    controlId: "saveButton",
    name: "saveButton",
    label: "Save",
    type: "submit",
    variant: "primary",
    visibleInDisplay: false,
  },
];

export const displayAccountFields = [
  ...accountFields,
  {
    controlId: "nhs_results",
    name: "nhs_results",
    label: "See your results in the NHS App",
    type: "text",
    placeholder: "",
    required: true,
    readOnly: true,
    className: "border-0 bg-transparent ps-0 shadow-none",
    helpTextAbove: (
      <>
        We've partnered with Patients Know Best (PKB) to securely share your
        test results with the NHS App.{" "}
        <a
          href="http://myhealthchecked.com/patients-know-best"
          target="_blank"
          rel="noreferrer"
        >
          Learn more
        </a>
        .
      </>
    ),
  },
  {
    controlId: "nhs_number",
    name: "nhs_number",
    label: "Verified NHS Number",
    type: "text",
    placeholder: "",
    required: true,
    readOnly: true,
    className: "border-0 bg-transparent ps-0 shadow-none",
    helpTextAbove: (
      <>
        If you believe your verified NHS number is incorrect, please report this
        to{" "}
        <a href="mailto:care@myhealthchecked.com">care@myhealthchecked.com</a>.
      </>
    ),
  },
];

export const testTypeFields = [
  // {
  //   controlId: "test_type",
  //   name: "test_type",
  //   type: "select",
  //   label: "",
  //   required: true,
  //   className: "form-group w-50 test_type_select",
  //   helpTextClass: "required",
  //   helpText: ``,
  //   options: [
  //     { value: TEST_TYPES["blood-cholesterol"], label: "Cholesterol Profile Test" },
  //     { value: TEST_TYPES["blood-energy"], label: "Energy Profile Test" },
  //     { value: TEST_TYPES["blood-general"], label: "General Health Test" },
  //     { value: TEST_TYPES["blood-heart"], label: "Heart Profile Test" },
  //     { value: "blood-thyroid", label: "Thyroid Profile Test" },
  //     {
  //       value: TEST_TYPES["blood-vitamins"],
  //       label: "Vitamins & Minerals Profile Test",
  //     },
  //     { value: TEST_TYPES["blood-weight"], label: "Weight Management Test" },
  //     { value: TEST_TYPES["blood-vitamin-b12"], label: "Vitamin B12 Test" },
  //     { value: TEST_TYPES["boots-blood-vitamin-b12"], label: "Vitamin B12 Test"},
  //     { value: TEST_TYPES["blood-testosterone"], label: "Testosterone Test" },
  //   ],
  //   readOnly: true,
  //   disabled: true,
  // },
  createSubmitButton(
    "Confirm & Continue",
    "",
    "Confirm & Continue btn-phlebotomist",
  ),
];

export const phlebotomyConfirmDetailFields = [
  createTextInput("firstname", "First name", "first_name", { maxLength: 63 }),
  createTextInput("lastname", "Last name", "last_name", { maxLength: 63 }),
  {
    controlId: "dob",
    label: "Date of birth",
    type: "date",
    className: "form-group",
    name: "dob",
    max: formattedDefaultDate,
    required: true,
    helpText: HELP_TEXTS.dob,
  },
  {
    controlId: "number",
    label: "Main phone number",
    type: "tel",
    className: "form-group",
    name: "phone_number_primary",
    maxLength: 14,
    required: true,
    helpText: HELP_TEXTS.phone,
  },
  createGenderField(),
  createGenderIdentityField(),
  createSubmitButton("Continue"),
];

const createDNAField = (
  name,
  label,
  dontEatLabel = "Don't Eat",
  eatLabel = "Eat",
) => ({
  name,
  label,
  options: [
    { label: dontEatLabel, value: "dont_eat" },
    { label: eatLabel, value: "eat" },
  ],
});

export const DNAAsstFields = [
  createDNAField("dairy", "Milk and dairy"),
  createDNAField("nuts", "Nuts"),
  createDNAField("gluten", "Gluten"),
  createDNAField("shellfish", "Shellfish"),
  createDNAField("oily_fish", "Oily Fish"),
  createDNAField("eggs", "Eggs"),
  createDNAField("poultry", "Poultry"),
  createDNAField("red_meat", "Red Meat excluding Pork"),
  createDNAField("pork", "Pork"),
  createDNAField("alcohol", "Alcohol", "Don't Drink", "Drink"),
];

export const DNAAsstSelectFields = [
  createSelectField(
    "1",
    "Milk and dairy",
    "dairy",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val1",
    },
    true,
  ),

  createSelectField(
    "2",
    "Nuts",
    "nuts",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val2",
    },
    true,
  ),

  createSelectField(
    "3",
    "Gluten",
    "gluten",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val3",
    },
    true,
  ),

  createSelectField(
    "4",
    "Shellfish",
    "shellfish",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val4",
    },
    true,
  ),

  createSelectField(
    "5",
    "Oily Fish",
    "oily_fish",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val5",
    },
    true,
  ),

  createSelectField(
    "6",
    "Eggs",
    "eggs",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val6",
    },
    true,
  ),

  createSelectField(
    "7",
    "Poultry",
    "poultry",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val7",
    },
    true,
  ),

  createSelectField(
    "8",
    "Red Meat excluding Pork",
    "red_meat",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val8",
    },
    true,
  ),

  createSelectField(
    "9",
    "Pork",
    "pork",
    [
      { value: "dont_eat", label: "Don't Eat" },
      { value: "eat", label: "Eat" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val9",
    },
    true,
  ),

  createSelectField(
    "10",
    "Alcohol",
    "alcohol",
    [
      { value: "dont_eat", label: "Don't Drink" },
      { value: "eat", label: "Drink" },
    ],
    {
      hide: false,
      placeholder: "Please select",
      hidePlaceholderOption: true,
      aria_describedby: "val10",
    },
    true,
  ),
];
export const contactFields = [
  createField({
    controlId: "1",
    label: "",
    type: "email",
    name: "email",
    placeholder: "Type here",
    aria_describedby: "val1",
  }),
  {
    controlId: "marketing_consent",
    name: "marketing_consent",
    label:
      "I agree to receive this guide and thyroid health updates from MyHealthChecked. I can unsubscribe at any time. See our Privacy Policy below.",
    type: "checkbox",
    helpText: "",
  },
  createSubmitButton("Submit"),
];

export const heightWeightFields = [
  {
    type: "section-card",
    title: "Height and weight",
    className: "mobile-card",
    children: [
        createField({
      controlId:"1",
      label: "Please enter your height (cm)",
      type: "number",
      name:"height",
      htmlFor: "height",
      maxLength: 4,
      aria_describedby: "val1",
      placeholder: "Eg., 175",
      className: "form-control intentional-required",
      required: true,
    }),
      createMeasurementField(
        "3",
        "Please enter your weight",
        "weight",
        [
          { value: "metric", label: "kg" },
          { value: "imperial", label: "lbs" },
        ],
        "weightUnits",
      ),
      {
        type: "inline-group",
        className: "mb-3",
        label: "Are you pregnant?",
        required: true,
        hide: true,
        children: [
          {
            name: "ispregnant",
            hide: true,
            controlId: "5",
            label: "",
            type: "toggle-buttons",
            convert: true,
            options: COMMON_OPTIONS.yesNoButtons,
            className: "toggle-btn-group",
            aria_describedby: "val5",
          },
        ],
      },
      createSelectField("15", "", "trimester", ["1", "2", "3"], {
        hide: true,
        placeholder: "Select trimester",
        aria_describedby: "val15",
        required: false,
      }),
    ],
  },
];

export const lifestyleFullFields = [
  {
    type: "section-card",
    title: "Smoking",
    className: "mobile-card body-form",
    children: [
      {
        controlId: "lifestyleId",
        name: "smoking",
        label: "Do you smoke cigarettes?",
        type: "radio",
        required: true,
        choices: [
          { value: "Yes", label: "Yes" },
          { value: "No", label: "No" },
          { value: "Ex-smoker", label: "Ex-smoker" },
        ],
        aria_describedby: "val6",
      },
      createSelectField(
        "7",
        "How many cigarettes (or equivalent) per day?",
        "cigarettes_per_day",
        ["0 - 5", "6 - 10", "11 - 20", "20+"],
        {
          hide: true,
          placeholder: "Select an option",
          aria_describedby: "val7",
        },
      ),
      createField({
        controlId: "8",
        label: "How many years did you smoke?",
        type: "number",
        name: "how_many_year",
        htmlFor: "how_many_year",
        maxLength: 3,
        placeholder: "Eg., 12 years",
        aria_describedby: "val8",
        hide: true,
      }),
      createField({
        controlId: "9",
        label: "How many per day on average?",
        type: "number",
        name: "how_many_day_average",
        htmlFor: "how_many_day_average",
        maxLength: 3,
        placeholder: "Eg., 12",
        aria_describedby: "val9",
        hide: true,
      }),
    ],
  },
  {
    type: "section-card",
    title: "Alcohol",
    className: "mobile-card body-form",
    children: [
      {
        controlId: "lifestyleId",
        name: "alcohol",
        label: "Do you drink alcohol?",
        type: "radio",
        required: true,
        choices: [
          { value: "Yes", label: "Yes" },
          { value: "No", label: "No" },
          { value: "I recently quit", label: "I recently quit" },
        ],
        aria_describedby: "val10",
      },
      createSelectField(
        "11",
        "How many units per week?",
        "alcohol_units_per_week",
        ["0 - 7", "7 - 14", "15 - 30", "30+"],
        {
          hide: true,
          placeholder: "Select an option",
          helpText: `If you're unsure how to calculate, visit: [NHS LINK](https://www.nhs.uk/Live-well/alcohol-advice/calculating-alcohol-units/)`,
          aria_describedby: "val11",
        },
      ),
    ],
  },
  {
    type: "section-card",
    title: "Physical activity",
    className: "mobile-card physical-activity-wrapper",
    children: [
      createRadioField(
        "lifestyleId",
        "Do you do any physical activity?",
        "physical_activity",
        COMMON_OPTIONS.yesNo.map((v) => ({ value: v, label: v })),
        { aria_label: "physical_activity" },
      ),
      createActivitySelectField(
        "2",
        "Light activity, e.g. walking, stretching (in hours)",
        "light_activity_hours_per_week",
      ),
      createActivitySelectField(
        "3",
        "Moderate activity, e.g. aerobics, golf (in hours)",
        "moderate_activity_hours_per_week",
      ),
      createActivitySelectField(
        "4",
        "Vigorous activity, e.g. running, weight training (in hours)",
        "vigorous_activity_hours_per_week",
      ),
    ],
  },
];

export const medicalFullFields = [
  ...medicalConditionFields.map((field) => {
    if (field.title === "Medical conditions") {
      const { title, ...rest } = field;
      return rest;
    }
    return field;
  }),
  ...medicationFields.map((field) => {
    if (field.title === "Medications") {
      return field;
    }
    return field;
  }),
  ...supplementFields
    .filter((field) => !field?.btnGroup)
    .map((field) => {
      if (field.title === "Supplementations") {
        return field;
      }
      return field;
    }),
];

export const changePasswordFields = [
  createField({
    controlId: "current-password",
    required: false,
    placeholder: "Enter current password",
    type: "password",
    name: "currentPassword",
    aria_describedby: "val23",
    maxLength: 16,
  }),
  createField({
    controlId: "password",
    required: false,
    placeholder: "New password",
    type: "password",
    name: "newPassword",
    aria_describedby: "val24",
    links: (
      <div className="forgot-password-link">
        Must be 8 characters, contain atleast 1 uppercase and 1 lowercase
        letter, and 1 number or special character
      </div>
    ),
    maxLength: 16,
  }),
  createField({
    controlId: "confirm-password",
    required: false,
    placeholder: "Confirm new password",
    type: "password",
    name: "confirmPassword",
    aria_describedby: "val25",
    maxLength: 16,
  }),
];
export const restestOrderKit = [
  {
    type: "group",
    className: "",
    children: createNameFields(true),
  },
  createAddressSelectField("home_street_address"),
  createTextInput("address_line_1", "Address line 1 ", "address_line_1", {
    maxLength: 256,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Address line 1",
    required: true,
    disabled: true,
    hide: true,
  }),
  createTextInput("address_line_2", "Address line 2", "address_line_2", {
    maxLength: 256,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Address line 2",
    required: false,
    disabled: true,
    hide: true,
  }),
  createTextInput("home_city", "Town/City", "home_city", {
    maxLength: 63,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Town/City",
    required: true,
    disabled: true,
    hide: true,
  }),
  createTextInput("home_country", "County/Region", "home_country", {
    maxLength: 3,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "County/Region",
    value: "UK",
    required: false,
    hide: true,
    disabled: true,
  }),
  createPostcodeField(),
];
export const restestOrderKitModal = [
  {
    type: "group",
    className: "",
    children: createNameFields(true),
  },
  createAddressSelectField("home_street_address", "", true),
  createTextInput("address_line_1", "Address line 1 ", "address_line_1", {
    maxLength: 256,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Address line 1",
    required: true,
    hide: true,
    disabled: true,
  }),
  createTextInput("address_line_2", "Address line 2", "address_line_2", {
    maxLength: 256,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Address line 2",
    required: false,
    hide: true,
    disabled: true,
  }),
  createTextInput("home_city", "Town/City", "home_city", {
    maxLength: 63,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "Town/City",
    required: true,
    hide: true,
    disabled: true,
  }),
  createTextInput("home_country", "County/Region", "home_country", {
    maxLength: 3,
    label: "",
    className: "w-100 w-md-50",
    placeholder: "County/Region",
    value: "UK",
    required: false,
    hide: true,
    isDisabled: true,
  }),
  createPostcodeField("", true),
  createConsentCheckbox(
    "8",
    "I understand the importance of carefully following the sample collection instructions upon retesting",
    "retest_instructions"
  ),
  createConsentCheckbox(
    "9",
    "I understand that refunds are not available after retesting for any missing results. Only one free retest is permitted.",
    "understand_refund_consent",
    true,
  ),
];